import { arrayMove } from "@dnd-kit/sortable";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import {
  apiDataFetchingAtom,
  applyRideMlPathIgnoredByWaypoints,
  areStationsDragReorderedAtom,
  buildRideMetadataAfterDragReorder,
  castErrorResult,
  checkStationsOrder,
  ConfirmResult,
  createErrorResult,
  createSuccessResultVoid,
  didRoutingWaypointOrderChange,
  DirectionEnum,
  errors,
  getStationComposedId,
  hasRoutingWaypointAtRouteEnds,
  HubRideActionKind,
  IReorderStationsPayload,
  IRideDto,
  IRideMetadata,
  isMlPathRoutingWaypoint,
  isSoftHardValidationSingleErrorResponse,
  isSoftValidationSingleErrorResponse,
  isStationRouteWaypointStation,
  IStationInfo,
  isUserOwnedRoutingWaypoint,
  lastSelectedRebuildTypeAtom,
  Log,
  mapHardValidationErrorToResult,
  RebuildRouteTypeEnum,
  reorderStationsByOrderedArray,
  rideHasActiveSavedMlPath,
  rideHasUnsavedStationChangeAtom,
  RidesApi,
  ridesDraggingStationsTempIdsAtom,
  RideStationPanelMode,
  SimpleResult,
  simpleResultErrorEmptyResponse,
  simpleResultFromException,
  simpleResultInvalidApiResponse,
  simpleResultTrue,
  TDragDropSortable,
  TStationComposedId,
  warnAndToastSimpleResult,
} from "@ryde/shared";

import { useModalConfirm } from "../../../hooks-singletons";
import { useSoftValidationModalConfirm } from "../../use-soft-validation-modal-confirms";
import { useHubRideActionConfirm } from "../hubs-logic";
import { useClearStaleRideEditDto } from "../use-clear-stale-ride-edit-dto";
import { useRebuildRoute } from "../use-rebuild-route";
import { useRideDtoActions } from "../use-ride-dto-actions";
import { useRideEdit } from "../use-ride-edit";
import { useRidesListActions } from "../use-rides-list-actions";
import {
  getIndexesFromSortable,
  getStationIdsFromSortable,
} from "./drag-ride-stations.utils";

export const useDragRideStationsReorder = () => {
  const { t } = useTranslation();
  const setStationsUpdating = useSetRecoilState(apiDataFetchingAtom);
  const { updateEditingRideMetadataRideDate } = useRideDtoActions();
  const { confirmSoftValidationAction } = useSoftValidationModalConfirm();

  const { saveRide } = useRideEdit();
  const { rebuildRideRoute } = useRebuildRoute();
  const { replaceRide } = useRidesListActions();
  const { clearStaleRideEditDtoCacheForRide } = useClearStaleRideEditDto();
  const setTempDragStationsIds = useSetRecoilState(
    ridesDraggingStationsTempIdsAtom,
  );
  const setStationsAreDragReordered = useSetRecoilState(
    areStationsDragReorderedAtom,
  );
  const setLastSelectedRebuildType = useSetRecoilState(
    lastSelectedRebuildTypeAtom,
  );
  const setRideHasUnsavedStationChange = useSetRecoilState(
    rideHasUnsavedStationChangeAtom,
  );

  const { confirmDialogResult } = useModalConfirm();
  const { confirmHubRideAction } = useHubRideActionConfirm();

  const updateStationsOrderRide = useEvent(
    async ({
      rideId,
      stationComposedIds,
      rideDto,
    }: {
      rideId: number;
      stationComposedIds: TStationComposedId[];
      rideDto?: Partial<IRideDto>;
    }): Promise<SimpleResult<void>> => {
      if (!stationComposedIds?.length) {
        return createErrorResult(errors.ride.errorReorderingStationRide);
      }

      if (!rideId) {
        return createErrorResult(errors.ride.invalidRideId);
      }

      const isHubActionConfirmed = await confirmHubRideAction({
        rides: [rideDto],
        actionKind: HubRideActionKind.Modify,
      });

      if (!isHubActionConfirmed) {
        return createErrorResult(errors.ride.reorderStationCanceledByUser);
      }

      const res = await confirmDialogResult({
        title: t("ride.CONFIRM_STATION_REORDER"),
        message: t("ride.CONFIRM_STATION_REORDER_MESSAGE"),
      });

      if (res !== ConfirmResult.OK) {
        return createErrorResult(errors.ride.reorderStationCanceledByUser);
      }

      try {
        setStationsUpdating(true);

        const apiPayload: IReorderStationsPayload = {
          rideId,
          stationComposedIds,
          isReturnDtoForGrid: true,
        };

        let rideUpdatedDtoResult = await RidesApi.reorderStations(apiPayload);

        if (isSoftValidationSingleErrorResponse(rideUpdatedDtoResult?.data)) {
          setStationsUpdating(false);

          const softValidationResult = await confirmSoftValidationAction(
            rideUpdatedDtoResult.data,
          );

          if (softValidationResult === ConfirmResult.CANCEL) {
            return createSuccessResultVoid();
          }

          setStationsUpdating(true);
          apiPayload.token = rideUpdatedDtoResult.data.token;
          rideUpdatedDtoResult = await RidesApi.reorderStations(apiPayload);
        } else if (
          isSoftHardValidationSingleErrorResponse(rideUpdatedDtoResult?.data)
        ) {
          const hardValidationError =
            rideUpdatedDtoResult.data.validationErrors[0];
          setStationsUpdating(false);
          const errorResult =
            mapHardValidationErrorToResult(hardValidationError);
          warnAndToastSimpleResult(errorResult, t);
          return createSuccessResultVoid();
        }

        if (!rideUpdatedDtoResult?.success) {
          return castErrorResult(rideUpdatedDtoResult as SimpleResult<void>);
        }

        if (!rideUpdatedDtoResult?.data) {
          return simpleResultErrorEmptyResponse(t);
        }

        replaceRide(rideUpdatedDtoResult.data as IRideDto);
        clearStaleRideEditDtoCacheForRide(rideId);
        Log.debug("Stations reordered successfully");
        return createSuccessResultVoid();
      } catch (error) {
        return simpleResultFromException({
          error,
          fallbackTranslationKey: errors.ride.errorReorderingStationRide,
        });
      } finally {
        setStationsUpdating(false);
      }
    },
  );

  const getRideIdFromSortable = (
    sortable: TDragDropSortable<TStationComposedId>,
  ): number => sortable?.payload?.rideDto?.id || 0;

  const validateRide = ({
    sortable,
    rideId,
    mode,
  }: {
    sortable: TDragDropSortable<TStationComposedId>;
    rideId: number;
    mode: RideStationPanelMode;
  }): SimpleResult<void> => {
    if (!rideId && mode === RideStationPanelMode.GRID_PANEL) {
      return createErrorResult(errors.ride.invalidRideForReorder);
    }

    const rideToId = sortable?.payload?.rideDto?.id || 0;

    if (rideId !== rideToId) {
      return new SimpleResult({
        success: false,
        error: t(errors.ride.cantReorderStationOfDifferentRides, {
          rideId1: rideId || "-",
          rideId2: rideToId || "-",
        }),
      });
    }

    if (!sortable?.payload?.rideDto) {
      return createErrorResult(
        errors.ride.cantReorderStationInvalidDragRideObject,
      );
    }

    return simpleResultTrue;
  };

  const validateStationOrder = (
    sortable: TDragDropSortable<TStationComposedId>,
    stationsUpdatedIds: TStationComposedId[],
  ): SimpleResult<void> => {
    const checkOrderInBeforeOut = checkStationsOrder(
      sortable?.payload?.rideDto?.metadata?.passengersInfo || [],
      stationsUpdatedIds,
    );

    if (!checkOrderInBeforeOut) {
      return createErrorResult(
        errors.ride.invalidStationOrderPickupAfterDropoff,
      );
    }

    const stationsInfo =
      sortable?.payload?.rideDto?.metadata?.stationsInfo || [];
    const reorderedStations = stationsUpdatedIds
      .map(composedId =>
        stationsInfo.find(
          station => getStationComposedId(station) === composedId,
        ),
      )
      .filter((station): station is IStationInfo => Boolean(station));

    if (hasRoutingWaypointAtRouteEnds(reorderedStations)) {
      return createErrorResult(
        errors.ride.routingWaypointCannotBeRouteEndpoint,
      );
    }

    return simpleResultTrue;
  };

  const didRideWaypointVisitOrderChange = (
    rideDto: Partial<IRideDto>,
    stationsUpdatedIds: TStationComposedId[],
    draggedStation?: IStationInfo,
  ): boolean => {
    const stationsInfo = rideDto.metadata?.stationsInfo ?? [];
    const stationByComposedId = new Map(
      stationsInfo.map(station => [getStationComposedId(station), station]),
    );

    return (
      isStationRouteWaypointStation(draggedStation) ||
      didRoutingWaypointOrderChange({
        previousOrderedIds: stationsInfo.map(getStationComposedId),
        nextOrderedIds: stationsUpdatedIds,
        isWaypointId: composedId =>
          isStationRouteWaypointStation(stationByComposedId.get(composedId)),
      })
    );
  };

  const handleDragResolver = async ({
    mode,
    rideId,
    rideDto,
    stationsUpdatedIds,
    draggedStation,
  }: {
    mode: RideStationPanelMode;
    rideId: number;
    rideDto: Partial<IRideDto>;
    stationsUpdatedIds: TStationComposedId[];
    draggedStation?: IStationInfo;
  }): Promise<SimpleResult<unknown>> => {
    if (mode === RideStationPanelMode.GRID_PANEL) {
      if (
        didRideWaypointVisitOrderChange(
          rideDto,
          stationsUpdatedIds,
          draggedStation,
        )
      ) {
        return handleWaypointCRUDTabMode({
          rideId,
          rideDto,
          stationsUpdatedIds,
        });
      }

      return updateStationsOrderRide?.({
        rideId,
        stationComposedIds: stationsUpdatedIds,
        rideDto,
      });
    }

    if (mode === RideStationPanelMode.CRUD_TAB && rideDto) {
      const draggedIsUserOwnedWaypoint =
        isUserOwnedRoutingWaypoint(draggedStation);
      const hasActiveSavedMlPath = rideHasActiveSavedMlPath(rideDto.metadata);

      if (
        isMlPathRoutingWaypoint(draggedStation) ||
        (hasActiveSavedMlPath && !draggedIsUserOwnedWaypoint)
      ) {
        return handleCRUDTabMode({ rideId, rideDto, stationsUpdatedIds });
      }

      if (
        didRideWaypointVisitOrderChange(
          rideDto,
          stationsUpdatedIds,
          draggedStation,
        )
      ) {
        return handleWaypointCRUDTabMode({
          rideId,
          rideDto,
          stationsUpdatedIds,
        });
      }

      return handleCRUDTabMode({ rideId, rideDto, stationsUpdatedIds });
    }

    return createErrorResult(errors.ride.invalidDragDropRideData);
  };

  const handleWaypointCRUDTabMode = async ({
    rideId,
    rideDto,
    stationsUpdatedIds,
  }: {
    rideId: number;
    rideDto: Partial<IRideDto>;
    stationsUpdatedIds: TStationComposedId[];
  }): Promise<SimpleResult<unknown>> => {
    const rideDtoForRebuild = rideHasActiveSavedMlPath(rideDto.metadata)
      ? {
          ...rideDto,
          metadata: applyRideMlPathIgnoredByWaypoints(rideDto.metadata ?? {}),
        }
      : rideDto;

    const metadataReordered =
      reorderStationsByOrderedArray({
        metadata: rideDtoForRebuild.metadata,
        stationNewOrderIds: stationsUpdatedIds,
        direction: rideDtoForRebuild.direction || DirectionEnum.PickUp,
      }) || {};

    // Drop stale polyline immediately so the map OSRM-previews the new visit order before recount returns.
    const metadataForMapPreview: IRideMetadata = {
      ...metadataReordered,
      routePolyline: undefined,
      distances: undefined,
      durations: undefined,
    };

    updateEditingRideMetadataRideDate({
      rideId,
      updatedMetadata: metadataForMapPreview,
      skipReorderStationsByTime: true,
    });
    setRideHasUnsavedStationChange(true);
    setStationsAreDragReordered(true);
    setLastSelectedRebuildType(RebuildRouteTypeEnum.KeepOrder);

    const rideWithReorderedWaypoints: Partial<IRideDto> = {
      ...rideDtoForRebuild,
      metadata: metadataForMapPreview,
    };

    return rebuildRideRoute(rideWithReorderedWaypoints, {
      rebuildType: RebuildRouteTypeEnum.KeepOrder,
      optimize: false,
    });
  };

  const handleCRUDTabMode = async ({
    rideId,
    rideDto,
    stationsUpdatedIds,
  }: {
    rideId: number;
    rideDto: Partial<IRideDto>;
    stationsUpdatedIds: TStationComposedId[];
  }): Promise<SimpleResult<unknown>> => {
    if ((rideDto.id || 0) !== rideId) {
      return createErrorResult(errors.ride.invalidRideDragIdMismatch);
    }

    const isHubActionConfirmed = await confirmHubRideAction({
      rides: [rideDto],
      actionKind: HubRideActionKind.Modify,
    });

    if (!isHubActionConfirmed) {
      return createErrorResult(errors.ride.reorderStationCanceledByUser);
    }

    const metadataRecounted =
      reorderStationsByOrderedArray({
        metadata: rideDto.metadata,
        stationNewOrderIds: stationsUpdatedIds,
        direction: rideDto.direction || DirectionEnum.PickUp,
      }) || {};
    Log.debug(
      "Composed metadata after station drag for checking dry save ride:",
      metadataRecounted,
    );

    const rideCheckedResult = await saveRide({
      rideDto,
      sendToSupplier: false,
      dryRun: true,
      stationsDragReordered: true,
      metadataDryRun: metadataRecounted,
      skipChangedValuesConfirmation: !rideDto.id,
      isReturnDtoForGrid: false,
    });

    if (!rideCheckedResult.success) {
      return rideCheckedResult;
    }

    const rideChecked = rideCheckedResult.data;

    if (rideDto.id && rideChecked?.id !== rideDto.id) {
      return createErrorResult(errors.ride.invalidRideIDBadAPIResponse);
    }

    Log.debug(
      "Stations from api:",
      rideChecked?.metadata?.stationsInfo?.map(getStationComposedId),
      "sent:",
      metadataRecounted.stationsInfo?.map(getStationComposedId),
      "old:",
      rideDto.metadata?.stationsInfo?.map(getStationComposedId),
    );
    Log.debug("Checked dry ride on station move from api ", rideChecked);

    if (!rideChecked?.metadata) {
      return simpleResultInvalidApiResponse(t);
    }

    setStationsAreDragReordered(true);
    setLastSelectedRebuildType(null);
    const metadataDragReordered = buildRideMetadataAfterDragReorder({
      clientReorderedMetadata: metadataRecounted,
      drySaveMetadata: rideChecked.metadata,
    });
    updateEditingRideMetadataRideDate({
      rideId,
      updatedMetadata: metadataDragReordered,
      skipReorderStationsByTime: true,
    });
    setRideHasUnsavedStationChange(true);
    return simpleResultTrue;
  };

  const dragReorderStationsRide = useEvent(
    async (
      source: TDragDropSortable<TStationComposedId>,
      destination: TDragDropSortable<TStationComposedId>,
    ): Promise<SimpleResult<unknown>> => {
      const rideId: number = getRideIdFromSortable(source);
      const mode: RideStationPanelMode =
        source?.mode || RideStationPanelMode.GRID_PANEL;

      const rideValidationResult = validateRide({
        sortable: source,
        rideId,
        mode,
      });

      if (!rideValidationResult.success) {
        return rideValidationResult;
      }

      const rideDto = source?.payload?.rideDto;
      const stationsOldComposedIds: TStationComposedId[] =
        getStationIdsFromSortable(source);
      const { oldIndex, newIndex }: { oldIndex: number; newIndex: number } =
        getIndexesFromSortable(source, destination);

      if (oldIndex === newIndex) {
        return createErrorResult(errors.ride.sameIndexReturnedNoNeedToReorder);
      }

      const stationsUpdatedIds: TStationComposedId[] =
        arrayMove<TStationComposedId>(
          stationsOldComposedIds,
          oldIndex,
          newIndex,
        );

      const stationOrderValidationResult = validateStationOrder(
        source,
        stationsUpdatedIds,
      );

      if (!stationOrderValidationResult.success) {
        return stationOrderValidationResult;
      }

      setTempDragStationsIds(stationsUpdatedIds);
      Log.debug(
        `Stations order before and after sorting for ride id ${rideId}:`,
        stationsOldComposedIds,
        stationsUpdatedIds,
      );

      const result = await handleDragResolver({
        mode,
        rideId,
        rideDto,
        stationsUpdatedIds,
        draggedStation: source?.payload?.station,
      });

      setTempDragStationsIds(undefined);
      return result;
    },
  );

  return { dragReorderStationsRide };
};
